import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { Textarea } from "../components/ui/textarea";
import {
  Heart,
  ArrowLeft,
  Loader2,
  CheckCircle2,
  Clock,
  DollarSign,
  Gift,
  UserCircle,
} from "lucide-react";
import { apiClient } from "../lib/apiClient";
import { DonationPolling, DonationSuccess, DonationCancelled, DonationFailed } from "../components/DonationStatusScreens";


const PRESET_AMOUNTS = [
  { id: "2", amount: 2, label: "$2" },
  { id: "5", amount: 5, label: "$5" },
  { id: "10", amount: 10, label: "$10" },
  { id: "25", amount: 25, label: "$25" },
  { id: "50", amount: 50, label: "$50" },
];

export default function DonatePage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const cancelled = searchParams.get("cancelled");

  const [user, setUser] = useState(null);
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState("");
  const [donorName, setDonorName] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null); // null | 'polling' | 'paid' | 'failed' | 'cancelled'
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [history, setHistory] = useState([]);
  const [recentDonations, setRecentDonations] = useState([]);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      const u = JSON.parse(userData);
      setUser(u);
      setDonorName(u.name || "");
    }
  }, []);

  // Load donation history for logged-in users
  useEffect(() => {
    if (user) {
      apiClient.get("donationsHistory").then(res => setHistory(res.data)).catch(() => { /* non-critical */ });
    }
    apiClient.get("donationsRecent").then(res => setRecentDonations(res.data)).catch(() => { /* non-critical */ });
  }, [user]);

  // Fix: catch block in DonatePage
  const pollPaymentStatus = useCallback(async (sid, attempts = 0) => {
    const maxAttempts = 8;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPaymentStatus("failed");
      return;
    }

    try {
      const res = await apiClient.get("donationsStatus", sid);
      const data = res.data;

      if (data.payment_status === "paid") {
        setPaymentStatus("paid");
        setPaymentDetails(data);
        // Refresh history
        if (user) {
          apiClient.get("donationsHistory").then(r => setHistory(r.data)).catch(() => { /* non-critical */ });
        }
        apiClient.get("donationsRecent").then(r => setRecentDonations(r.data)).catch(() => { /* non-critical */ });
        return;
      }

      if (data.status === "expired") {
        setPaymentStatus("failed");
        return;
      }

      setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
    } catch {
      if (attempts < maxAttempts - 1) {
        setTimeout(() => pollPaymentStatus(sid, attempts + 1), pollInterval);
      } else {
        setPaymentStatus("failed");
      }
    }
  }, [user]);  

  useEffect(() => {
    if (sessionId) {
      setPaymentStatus("polling");
      pollPaymentStatus(sessionId);
    } else if (cancelled) {
      setPaymentStatus("cancelled");
    }
  }, [sessionId, cancelled, pollPaymentStatus]);

  const getSelectedAmountValue = () => {
    if (selectedAmount === "custom") {
      return parseFloat(customAmount) || 0;
    }
    const preset = PRESET_AMOUNTS.find(p => p.id === selectedAmount);
    return preset ? preset.amount : 0;
  };

  const handleDonate = async () => {
    const amount = getSelectedAmountValue();
    if (amount < 1) return;

    setLoading(true);
    try {
      const payload = {
        origin_url: window.location.origin,
        donor_name: donorName.trim() || "Anonymous",
        message: message.trim() || "",
      };

      if (selectedAmount === "custom") {
        payload.custom_amount = amount;
      } else {
        payload.package_id = selectedAmount;
      }

      const res = await apiClient.post("donationsCheckout", payload);
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to initiate donation. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  // Payment status screens
  if (paymentStatus === "polling") {
    return <DonationPolling />;
  }

  if (paymentStatus === "paid") {
    return <DonationSuccess paymentDetails={paymentDetails} user={user} onReset={() => setPaymentStatus(null)} />;
  }

  if (paymentStatus === "cancelled") {
    return <DonationCancelled onReset={() => setPaymentStatus(null)} />;
  }

  if (paymentStatus === "failed") {
    return <DonationFailed onReset={() => setPaymentStatus(null)} />;
  }

  // Main donation form
  return (
    <div className="min-h-screen bg-stone-light noise-bg">
      {/* Navigation */}
      <nav className="bg-white border-b border-stone sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link
              to={user ? "/dashboard" : "/"}
              className="flex items-center gap-2 text-stone-dark/60 hover:text-stone-dark transition-colors"
              data-testid="back-link"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">{user ? "Dashboard" : "Home"}</span>
            </Link>
            <div className="h-6 w-px bg-stone" />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-sage flex items-center justify-center">
                <Heart className="w-4 h-4 text-white" />
              </div>
              <span className="font-serif font-semibold text-base sm:text-lg text-stone-dark">Donate</span>
            </div>
          </div>
          {!user && (
            <Link to="/login">
              <Button variant="ghost" className="text-sage" data-testid="login-link">Log In</Button>
            </Link>
          )}
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-full bg-sage/10 flex items-center justify-center mx-auto mb-4">
            <Gift className="w-8 h-8 text-sage" />
          </div>
          <h1 className="font-serif text-2xl sm:text-3xl font-semibold text-stone-dark mb-2" data-testid="donate-title">
            Support Prayer Tracker
          </h1>
          <p className="text-sm sm:text-base text-stone-dark/60 max-w-md mx-auto">
            Your donation helps us keep this tool free and available for prayer communities everywhere.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Donation Form */}
          <div className="md:col-span-2">
            <Card className="border-stone">
              <CardContent className="p-4 sm:p-6">
                {/* Amount Selection */}
                <div className="mb-6">
                  <Label className="text-sm font-medium text-stone-dark mb-3 block">Select Amount</Label>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-3" data-testid="amount-grid">
                    {PRESET_AMOUNTS.map((preset) => (
                      <button
                        key={preset.id}
                        onClick={() => { setSelectedAmount(preset.id); setCustomAmount(""); }}
                        className={`py-3 px-2 rounded-xl text-center font-semibold transition-all border-2 ${
                          selectedAmount === preset.id
                            ? "border-sage bg-sage text-white"
                            : "border-stone bg-white text-stone-dark hover:border-sage/50"
                        }`}
                        data-testid={`amount-${preset.id}`}
                      >
                        {preset.label}
                      </button>
                    ))}
                    <button
                      onClick={() => setSelectedAmount("custom")}
                      className={`py-3 px-2 rounded-xl text-center font-semibold transition-all border-2 col-span-3 sm:col-span-5 ${
                        selectedAmount === "custom"
                          ? "border-sage bg-sage/5 text-sage"
                          : "border-stone bg-white text-stone-dark hover:border-sage/50"
                      }`}
                      data-testid="amount-custom"
                    >
                      Custom Amount
                    </button>
                  </div>

                  {selectedAmount === "custom" && (
                    <div className="relative mt-3">
                      <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-dark/40" />
                      <Input
                        type="number"
                        min="1"
                        max="10000"
                        step="0.01"
                        placeholder="Enter amount"
                        value={customAmount}
                        onChange={(e) => setCustomAmount(e.target.value)}
                        className="pl-8 text-lg"
                        data-testid="custom-amount-input"
                      />
                    </div>
                  )}
                </div>

                {/* Donor Info */}
                <div className="space-y-4 mb-6">
                  <div className="space-y-2">
                    <Label htmlFor="donorName">Your Name (optional)</Label>
                    <Input
                      id="donorName"
                      placeholder="Anonymous"
                      value={donorName}
                      onChange={(e) => setDonorName(e.target.value)}
                      data-testid="donor-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message">Message (optional)</Label>
                    <Textarea
                      id="message"
                      placeholder="Leave a note with your donation..."
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      className="min-h-16"
                      data-testid="donor-message-input"
                    />
                  </div>
                </div>

                {/* Donate Button */}
                <Button
                  onClick={handleDonate}
                  disabled={loading || !selectedAmount || (selectedAmount === "custom" && (!customAmount || parseFloat(customAmount) < 1))}
                  className="w-full bg-sage hover:bg-sage-dark text-white rounded-full py-6 text-base font-medium"
                  data-testid="donate-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      <Heart className="w-5 h-5 mr-2" />
                      Donate {getSelectedAmountValue() >= 1 ? `$${getSelectedAmountValue().toFixed(2)}` : ""}
                    </>
                  )}
                </Button>

                <p className="text-xs text-stone-dark/40 text-center mt-3">
                  Secure payment powered by Stripe. You'll be redirected to complete your donation.
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Recent Donations */}
            {recentDonations.length > 0 && (
              <Card className="border-stone">
                <CardContent className="p-4">
                  <h3 className="font-serif font-semibold text-stone-dark mb-3 text-sm">Recent Donations</h3>
                  <div className="space-y-2">
                    {recentDonations.slice(0, 5).map((d) => (
                      <div key={d.id || d.created_at} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2 min-w-0">
                          <UserCircle className="w-4 h-4 text-stone-dark/30 shrink-0" />
                          <span className="text-stone-dark/70 truncate">{d.donor_name || "Anonymous"}</span>
                        </div>
                        <span className="font-medium text-sage shrink-0">${d.amount?.toFixed(2)}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Donation History (logged-in users) */}
            {user && history.length > 0 && (
              <Card className="border-stone">
                <CardContent className="p-4">
                  <h3 className="font-serif font-semibold text-stone-dark mb-3 text-sm">Your Donations</h3>
                  <div className="space-y-2">
                    {history.slice(0, 5).map((d) => (
                      <div key={d.id || d.created_at} className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          {d.payment_status === "paid" ? (
                            <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                          ) : (
                            <Clock className="w-4 h-4 text-stone-dark/30 shrink-0" />
                          )}
                          <span className="text-stone-dark/50 text-xs">{formatDate(d.created_at)}</span>
                        </div>
                        <span className={`font-medium ${d.payment_status === "paid" ? "text-sage" : "text-stone-dark/40"}`}>
                          ${d.amount?.toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
